import { sample } from 'effector';
import { labmateClient } from '../../../../shared/configs/socket-client';
import { bluetoothEntity, BtScanResultPayload } from '../../../../entities/bluetooth/model';
import {
  devicesEntity,
  SensorDataPayload,
} from '@entities/devices';
import { Device } from 'shared/configs/device-types';
import { bluetoothConnectModel } from '@features/bluetooth/connect-device/model';
import { LabmateSdkParams } from '@shared/lib/labmate-sdk/types';
import { not } from 'patronum';


const labmateDevicesList: Device[] = [
  {
    key: "EN_FORCE_PLUS_MINUS_10N",
    name: "Force sensor",
    unit: "N",
    icon: "🔥",
  },
  {
    key: "EN_VOLTAGE_PLUS_MINUS_25V",
    name: "Voltage sensor",
    unit: "V",
    icon: "⚡",
  },
  {
    key: "EN_TEMP_MINUS_40_PLUS_140_C",
    name: "Temperature sensor",
    unit: "°C",
    icon: "🌡️",
  },
]

const onBluetoothDevicesScanResult = labmateClient.onEvent<{ devices: string[] }>("onBleDevicesDiscovered")

sample({
  clock: onBluetoothDevicesScanResult,
  target: labmateClient.getSensorsListFx,
});


sample({
  clock: labmateClient.getSensorsListFx.doneData,
  fn: (foundSensorsList): BtScanResultPayload => {
    return labmateDevicesList
      .map(device => {
        const foundSensor = foundSensorsList?.find(sensor => sensor.userSensorId === device.key)

        if (!foundSensor) return null

        return {
          mac: foundSensor.channelNumber.toString(),
          name: device.name,
        }
      })
      .filter(Boolean) as BtScanResultPayload
  },
  target: bluetoothEntity.setDevices,
})

const onDataArrived = labmateClient.onEvent<{ mappedData: Array<{ value: number, sensor_type: string }> }>("onDataPacketArrived")

sample({
  clock: onDataArrived,
  filter: not(labmateClient.disconnectFromSensorFx.pending),
  fn: ({ mappedData }) => {
    const data = mappedData[0];

    if (!data) throw new Error('No data arrived')

    const sensor = labmateDevicesList.find(device => device.key === data.sensor_type)

    if (!sensor) throw new Error('Sensor not supported')

    return sensor;
  },
  target: devicesEntity.setDeviceInfo,
})
sample({
  clock: onDataArrived,
  filter: not(labmateClient.disconnectFromSensorFx.pending),
  fn: ({ mappedData }): SensorDataPayload => {
    const data = mappedData[0];

    if (!data) throw new Error('No data arrived')

    const sensor = labmateDevicesList.find(device => device.key === data.sensor_type)

    if (!sensor) throw new Error('Sensor not supported')

    const device = {
      deviceId: "labmate",
      sensorId: sensor.key,
      value: data.value,
      deviceName: sensor.name,
      friendlyName: sensor.name,
      timestamp: new Date().toISOString(),
    };

    return device;

  },
  target: devicesEntity.onSensorDataUpdated,
})

sample({
  clock: bluetoothConnectModel.disconnectFromDevice,
  target: labmateClient.disconnectFromSensorFx,
});

sample({
  clock: labmateClient.disconnectFromSensorFx.done,
  filter: not(labmateClient.changeFrequencyFx.pending),
  target: devicesEntity.resetDomain,
});

sample({
  source: devicesEntity.$frequency,
  clock: bluetoothConnectModel.connectToDevice,
  fn: (frequency, { address }): LabmateSdkParams.ConnectToSensorParams => ({
    channelNumbers: [Number(address)],
    frequency,
  }),
  target: labmateClient.connectToSensorFx,
});

sample({
  clock: devicesEntity.$frequency,
  filter: Boolean,
  target: labmateClient.changeFrequencyFx,
})


devicesEntity.$deviceInfo.watch(console.log)